import { useState } from "react";
import { Modal } from "react-bootstrap";
import { Sparkles, BarChart2, X } from "lucide-react";

/* ─────────────────────────────────────────────────────── */
/*  CreateGraphModal — ALL original logic preserved        */
/*  Props: show, onClose, onCreateWithAI, onCreateCustom  */
/* ─────────────────────────────────────────────────────── */
const CreateGraphModal = ({ show, onClose, onCreateWithAI, onCreateCustom }) => {
  const [aiHov, setAiHov] = useState(false);
  const [custHov, setCustHov] = useState(false);

  return (
    <>
      <style>{`
        @keyframes cgm-fade {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to   { opacity: 1; transform: none; }
        }
        .cgm-card {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
          padding: 20px 22px;
          border-radius: 13px;
          border: 1.5px solid transparent;
          cursor: pointer;
          transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
          text-align: left;
          // background: #fff;
          animation: cgm-fade 0.3s ease both;
          width: 100%;
          outline: none;
          font-family: 'DM Sans', system-ui, sans-serif;
        }
        .cgm-card:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; }
      `}</style>


      <Modal
        show={show}
        onHide={onClose}
        centered
        backdrop="static"
        keyboard={false}
        contentClassName="border-0"
      >

        

          <div style={{
            padding: "20px 24px 18px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div>
              <div style={{
                fontSize: 11, letterSpacing: "0.1em", fontWeight: 700,
                color: "#10b981", textTransform: "uppercase",
                fontFamily: "'DM Mono', monospace", marginBottom: 4,
              }}>
                ESG Dashboard
              </div>
              <div style={{
                fontSize: 20, fontWeight: 800, color: "#0f172a",
                letterSpacing: "-0.02em", lineHeight: 1.2,
              }}>
                Create Graph
              </div>
            </div>

            {/* ── ORIGINAL: onClose handler ── */}
            <button
              onClick={onClose}
              style={{
                width: 34, height: 34, borderRadius: 9,
                border: "1px solid #e2e8f0",
                background: "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "#94a3b8",
                transition: "background 0.12s, color 0.12s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.color = "#ef4444"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94a3b8"; }}
            >
              <X size={16} />
            </button>
          </div>

          {/* ─── Body ─── */}
          <div style={{ padding: "22px 24px" }}>
            <p style={{
              fontSize: 13.5, color: "#64748b", margin: "0 0 20px",
              lineHeight: 1.6, fontWeight: 400,
            }}>
              Choose how you want to build your graph.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

              {/* ── AI Card — ORIGINAL onCreateWithAI ── */}
              <button
                className="cgm-card"
                onClick={onCreateWithAI}
                onMouseEnter={() => setAiHov(true)}
                onMouseLeave={() => setAiHov(false)}
                style={{
                  border: `1.5px solid ${aiHov ? "#3b82f6" : "#e2e8f0"}`,
                  background: aiHov ? "#eff6ff" : "#fafafa",
                  transform: aiHov ? "translateY(-1px)" : "none",
                  boxShadow: aiHov ? "0 8px 24px rgba(59,130,246,0.12)" : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: aiHov ? "#3b82f6" : "#dbeafe",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.18s", flexShrink: 0,
                  }}>
                    <Sparkles size={18} color={aiHov ? "#fff" : "#3b82f6"} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em", marginBottom: 2 }}>
                      Create with AI
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>
                      Let AI analyze your ESG data and suggest the best visualizations
                    </div>
                  </div>
                </div>
                {/* Arrow indicator */}
                <div style={{
                  alignSelf: "flex-end",
                  fontSize: 12, color: aiHov ? "#3b82f6" : "#cbd5e1",
                  fontWeight: 700, transition: "color 0.15s",
                  fontFamily: "'DM Mono', monospace",
                }}>
                  {aiHov ? "→" : "·"}
                </div>
              </button>

              {/* ── Custom Card — ORIGINAL onCreateCustom ── */}
              <button
                className="cgm-card"
                onClick={onCreateCustom}
                onMouseEnter={() => setCustHov(true)}
                onMouseLeave={() => setCustHov(false)}
                style={{
                  border: `1.5px solid ${custHov ? "#10b981" : "#e2e8f0"}`,
                  background: custHov ? "#f0fdf4" : "#fafafa",
                  transform: custHov ? "translateY(-1px)" : "none",
                  boxShadow: custHov ? "0 8px 24px rgba(16,185,129,0.12)" : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: custHov ? "#10b981" : "#d1fae5",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.18s", flexShrink: 0,
                  }}>
                    <BarChart2 size={18} color={custHov ? "#fff" : "#10b981"} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em", marginBottom: 2 }}>
                      Custom Graph
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>
                      Manually configure filters, axes, and chart type
                    </div>
                  </div>
                </div>
                <div style={{
                  alignSelf: "flex-end",
                  fontSize: 12, color: custHov ? "#10b981" : "#cbd5e1",
                  fontWeight: 700, transition: "color 0.15s",
                  fontFamily: "'DM Mono', monospace",
                }}>
                  {custHov ? "→" : "·"}
                </div>
              </button>
            </div>
          </div>
       
      </Modal>
    </>
  );
};

export default CreateGraphModal;